import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import ApplicantFilter from '@company/types/applicant-filter.type';
import { CandidateStage } from '@company/types/job-overview.type';

const baseCandidateFilters: ApplicantFilter = {
  nameOrEmail: '',
  database: undefined,
  gender: undefined,
  country: undefined,
  willingToTravel: undefined,
  travelLongerPeriod: undefined,
  sortBy: undefined,
  order: undefined,
};

// Only Applicants is sortable in the BE (other endpoints 500 on a sort param).
const DEFAULT_TAB_SORT: Partial<
  Record<CandidateStage, Pick<ApplicantFilter, 'sortBy' | 'order'>>
> = {
  [CandidateStage.APPLICANTS]: { sortBy: 'dateApplied', order: 'desc' },
};

export const defaultCandidateFilters = (
  tab: CandidateStage
): ApplicantFilter => ({
  ...baseCandidateFilters,
  ...DEFAULT_TAB_SORT[tab],
});

// Filters persist per tab (MyJobs pattern); seed every tab so slices are stable.
const initialFiltersByTab = (): Record<CandidateStage, ApplicantFilter> =>
  Object.values(CandidateStage).reduce(
    (acc, tab) => {
      acc[tab] = defaultCandidateFilters(tab);
      return acc;
    },
    {} as Record<CandidateStage, ApplicantFilter>
  );

export const initialState = {
  selectedWorkerId: null as number | null,
  shareWorkerId: null as number | null,
  activeTab: CandidateStage.NEW,
  candidateFiltersByTab: initialFiltersByTab(),
};

interface JobDetailStore {
  selectedWorkerId: number | null;
  shareWorkerId: number | null;
  activeTab: CandidateStage;
  candidateFiltersByTab: Record<CandidateStage, ApplicantFilter>;
  openWorkerModal: (workerId: number) => void;
  closeWorkerModal: () => void;
  openShareModal: (workerId: number) => void;
  closeShareModal: () => void;
  setActiveTab: (tab: CandidateStage) => void;
  setCandidateFilters: (filters: ApplicantFilter) => void;
  resetCandidateFilters: () => void;
}

export const useJobDetailStore = create<JobDetailStore>()(
  devtools(
    (set) => ({
      ...initialState,
      openWorkerModal: (workerId) => set({ selectedWorkerId: workerId }),
      closeWorkerModal: () => set({ selectedWorkerId: null }),
      openShareModal: (workerId) => set({ shareWorkerId: workerId }),
      closeShareModal: () => set({ shareWorkerId: null }),
      setActiveTab: (activeTab) => set({ activeTab }),
      setCandidateFilters: (filters) =>
        set((state) => ({
          candidateFiltersByTab: {
            ...state.candidateFiltersByTab,
            [state.activeTab]: filters,
          },
        })),
      resetCandidateFilters: () =>
        set((state) => ({
          candidateFiltersByTab: {
            ...state.candidateFiltersByTab,
            [state.activeTab]: defaultCandidateFilters(state.activeTab),
          },
        })),
    }),
    { name: 'job-detail-store' }
  )
);

export const useActiveCandidateFilters = (): ApplicantFilter =>
  useJobDetailStore((state) => state.candidateFiltersByTab[state.activeTab]);
